import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import config from '../../config';
import * as XLSX from 'xlsx';
import { DateRangePicker } from 'react-date-range';
import ModalEditRemarks from './ModalEditRemarks';
import ModalLoan from './ModalLoan';
import FailedMessage from './FailedMessage';
import SettlementInput from './SettlementInput';

const SLAB_OPTIONS = [
  { label: 'All',          value: '' },
  { label: '1–30 days',    value: '1-30' },
  { label: '31–60 days',   value: '31-60' },
  { label: '61–90 days',   value: '61-90' },
  { label: '91–120 days',  value: '91-120' },
  { label: '121–180 days', value: '121-180' },
  { label: '181–365 days', value: '181-365' },
  { label: '365+ days',    value: '365' },
];

// Show up to this many numeric page buttons in the pagination strip
const PAGE_WINDOW = 5;
const PAGE_SIZE   = 10;   // must match backend pageSize for non-excel

const ManagerLoanDetails = () => {
  // ── Data state ──────────────────────────────────────────────────────────────
  const [data, setData]               = useState([]);
  const [totalPages, setTotalPages]   = useState(0);
  const [totalCount, setTotalCount]   = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // ── Filter state ────────────────────────────────────────────────────────────
  const [selectedSlab, setSelectedSlab]       = useState('');
  const [selectedOptions, setSelectedOptions] = useState('');
  const [searchText, setSearchText]           = useState('');     // for mobile / loan id
  const [AgencySelection, setAgencySelection] = useState('');     // for State
  const [selectionRange, setSelectionRange]   = useState({ startDate: null, endDate: null, key: 'selection' });
  const [showCalendar, setShowCalendar]       = useState(false);
  const [openDate, setOpenDate]               = useState(false);

  // Filters that have actually been applied (used by fetchData).
  // Separating "draft" from "applied" prevents re-fetches while user is typing.
  const [appliedFilters, setAppliedFilters] = useState({
    searchType: '',
    searchValue: '',
    startDate: null,
    endDate: null,
  });

  // ── Waiver / Settlement state ─────────────────────────────────────────────
  const [loan, setLoan]                         = useState('');
  const [loan_data, setloanData]                = useState();
  const [percentage, setPercentage]             = useState();
  const [settlement, setSettlement]             = useState();
  const [settlementData, setSettlementData]     = useState('');
  const [settlementAmount, setSettlementAmount] = useState('');
  const [errorMsg, setErrorMsg]                 = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [linkData, setLinkData]                 = useState('');
  const [waiverCheckData, setWaiverCheckData]   = useState(null);
  const [showWaiverInfoModal, setShowWaiverInfoModal] = useState(false);
  const serviceSelect = useRef(null);
  const dailySelect   = useRef(null);
  const modalRef      = useRef();

  // ── Modal state ───────────────────────────────────────────────────────────
  const [showModal, setShowModal]           = useState(false);
  const [showModal1, setShowModal1]         = useState(false);
  const [selectedLoanId, setSelectedLoanId] = useState(null);
  const [loan_no, setLoan_no]               = useState('');
  const [cog_id, setCog_id]                 = useState();
  const [message, setMessage]               = useState();
  const [loadingbutton, setloadingbutton]   = useState(false);

  // ── UI state ─────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  // ── Redux ─────────────────────────────────────────────────────────────────────
  const user_id    = useSelector((state) => state.user.currentUser);
  const individual = useSelector((state) => state.Individual.individual_role);

  // ── Spinner styles ────────────────────────────────────────────────────────────
  const spinnerStyle = {
    border: '4px solid rgba(255,255,255,0.3)',
    borderTop: '4px solid #3498db',
    borderRadius: '50%',
    width: '24px',
    height: '24px',
    animation: 'spin 1s linear infinite',
    marginRight: '8px',
  };
  const loadingTextStyle = { display: 'flex', alignItems: 'center' };

  useEffect(() => {
    try {
      const styleSheet = document.styleSheets[0];
      const rule = '@keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }';
      styleSheet.insertRule(rule, styleSheet.cssRules.length);
    } catch (_) {}
  }, []);

  // ── Reset waiver modal selects on open ────────────────────────────────────
  useEffect(() => {
    const modal = document.getElementById('exampleModal');
    if (!modal) return;
    const resetModal = () => {
      if (serviceSelect.current) serviceSelect.current.value = 'service';
      if (dailySelect.current)   dailySelect.current.value   = 'daily';
    };
    modal.addEventListener('show.bs.modal', resetModal);
    return () => modal.removeEventListener('show.bs.modal', resetModal);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // API — now includes search filter params
  // ─────────────────────────────────────────────────────────────────────────────
  const buildUrl = (page, isExcel = false, filtersOverride = null) => {
    const token   = localStorage.getItem('access_token');
    const params  = new URLSearchParams();
    const filters = filtersOverride || appliedFilters;

    params.set('pages', page);
    if (selectedSlab)              params.set('days', selectedSlab);
    if (isExcel)                   params.set('excel', 'true');
    if (filters.searchType)        params.set('searchType', filters.searchType);
    if (filters.searchValue)       params.set('searchValue', filters.searchValue);
    if (filters.startDate)         params.set('startDate', new Date(filters.startDate).toISOString());
    if (filters.endDate)           params.set('endDate', new Date(filters.endDate).toISOString());

    return { url: `${config.apiUrlBetaV1}subAdmin?${params.toString()}`, token };
  };

  const fetchData = async (page = currentPage, filtersOverride = null) => {
    setLoading(true);
    setError(null);
    try {
      const { url, token } = buildUrl(page, false, filtersOverride);
      const res  = await fetch(url, {
        method: 'GET',
        headers: { Authorization: token, 'Content-Type': 'application/json' }
      });
      const json = await res.json();
      if (!json.status) {
        setData([]);
        setTotalPages(0);
        setTotalCount(0);
      } else {
        setData(json.data || []);
        setTotalPages(json.totalPages || 0);
        setTotalCount(json.totalLoansCount || 0);
      }

      // fetch waiver loan data if loan is set
      if (loan && loan !== 'undefined' && loan.length > 0) {
        const token2 = localStorage.getItem('access_token');
        const lRes = await fetch(
          `${config.apiUrlBetaV2}get_waiver/${loan}?percInterest=${Number(dailySelect.current?.value || 0)}&serperInterest=${Number(serviceSelect.current?.value || 0)}`,
          { method: 'GET', headers: { Authorization: token2, 'Content-Type': 'application/json' } }
        );
        const lJson = await lRes.json();
        setloanData(lJson.data);
        setPercentage(lJson?.data1);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch when page, slab, applied filters, or loan changes
  useEffect(() => {
    fetchData(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedSlab, appliedFilters, loan]);

  // ── Pagination helpers (smooth, fixed-width window) ──────────────────────────
  const handlePageClick = (page) => {
    if (page === '...') return;
    if (typeof page !== 'number') return;
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
  };

  // Always returns a fixed-width window so buttons don't jump around.
  // Example for PAGE_WINDOW=5:
  //  total=20, current=1  -> [1,2,3,4,5, ..., 20]
  //  total=20, current=10 -> [1, ..., 8,9,10,11,12, ..., 20]
  //  total=20, current=20 -> [1, ..., 16,17,18,19,20]
  const getPageNumbers = () => {
    if (totalPages <= 1) return [];
    const pages = [];

    if (totalPages <= PAGE_WINDOW + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    const half = Math.floor(PAGE_WINDOW / 2);
    let left   = currentPage - half;
    let right  = currentPage + half;

    if (left < 1)              { right += (1 - left); left = 1; }
    if (right > totalPages)    { left  -= (right - totalPages); right = totalPages; }
    left  = Math.max(1, left);
    right = Math.min(totalPages, right);

    if (left > 1) {
      pages.push(1);
      if (left > 2) pages.push('...');
    }
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages) {
      if (right < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  // ── Slab change ───────────────────────────────────────────────────────────────
  const handleSlabChange = (e) => {
    setSelectedSlab(e.target.value);
    setCurrentPage(1);
  };

  // ── Apply / clear search ─────────────────────────────────────────────────────
  const handleSearch = () => {
    const next = {
      searchType: selectedOptions || '',
      searchValue: '',
      startDate: null,
      endDate: null,
    };

    if (selectedOptions === 'LoanDate' || selectedOptions === 'DueDate') {
      if (selectionRange.startDate && selectionRange.endDate) {
        next.startDate = selectionRange.startDate;
        next.endDate   = selectionRange.endDate;
      } else {
        // No dates picked yet — don't apply
        return;
      }
    } else if (selectedOptions === 'State') {
      if (!AgencySelection) return;
      next.searchValue = AgencySelection;
    } else if (selectedOptions === 'Mobile_Number' || selectedOptions === 'Loan_Id') {
      if (!searchText.trim()) return;
      next.searchValue = searchText.trim();
    } else {
      // No search type selected — just reset to first page
      next.searchType = '';
    }

    setCurrentPage(1);
    setAppliedFilters(next);
  };

  const handleClearSearch = () => {
    setSelectedOptions('');
    setSearchText('');
    setAgencySelection('');
    setSelectionRange({ startDate: null, endDate: null, key: 'selection' });
    setSelectedSlab('');
    setCurrentPage(1);
    setAppliedFilters({ searchType: '', searchValue: '', startDate: null, endDate: null });
  };

  // ── Export ────────────────────────────────────────────────────────────────────
  const fetchPageForExport = async (page) => {
    const { url, token } = buildUrl(page, true);
    const res = await fetch(url, {
      method: 'GET',
      headers: { Authorization: token, 'Content-Type': 'application/json' }
    });
    return res.json();
  };

  const handleExport = async () => {
    setloadingbutton(true);
    try {
      const first = await fetchPageForExport(1);
      if (!first.status) { setloadingbutton(false); return; }
      let allData = first.data || [];
      const pages = first.totalPages || 1;
      for (let p = 2; p <= pages; p++) {
        const next = await fetchPageForExport(p);
        if (next.data) allData = [...allData, ...next.data];
      }
      const ws = XLSX.utils.json_to_sheet(allData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'SubAdmin Loans');
      XLSX.writeFile(wb, 'subadmin_loans.xlsx');
    } catch (err) {
      setError('Export failed: ' + err.message);
    } finally {
      setloadingbutton(false);
    }
  };

  // ── Waiver ────────────────────────────────────────────────────────────────────
  const handleWaiverClick = async (loan_id) => {
    const token = localStorage.getItem('access_token');
    try {
      const res  = await fetch(`${config.apiUrlBetaV1}check_waiver?loan_id=${loan_id}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Authorization': token },
      });
      const json = await res.json();
      let errorData = null;
      if (json?.error) errorData = typeof json.error === 'string' ? JSON.parse(json.error) : json.error;
      if (errorData && errorData?.payment_genration === true) {
        setWaiverCheckData(errorData);
        setLoan(String(loan_id));
        setShowWaiverInfoModal(true);
      } else {
        setLoan(String(loan_id));
        setTimeout(() => {
          const modalEl = document.getElementById('exampleModal');
          if (modalEl) new window.bootstrap.Modal(modalEl).show();
        }, 100);
      }
    } catch (err) {
      setLoan(String(loan_id));
      setTimeout(() => {
        const modalEl = document.getElementById('exampleModal');
        if (modalEl) new window.bootstrap.Modal(modalEl).show();
      }, 100);
    }
  };

  const handlecashfree = () => fetchData();

  const handlecashfreelink = async (loan_amount, loan_id, daily_late, service_charge, Payable_amount) => {
    setLoading(true);
    const token = localStorage.getItem('access_token');
    let postData = {
      loan_id,
      link_amount: loan_amount,
      daily_late,
      service_charge,
      waived_amount: Number((Number(Payable_amount) - Number(loan_amount)).toFixed(2)),
    };
    if (settlement?.setlement) postData['settlement'] = true;
    const response = await fetch(`${config.apiUrlBetaV2}cashfree_link`, {
      method: 'POST',
      headers: { 'Authorization': token, 'Content-Type': 'application/json' },
      body: JSON.stringify(postData),
    });
    if (response.ok) { setLoading(false); setSettlement(null); }
  };

  // ── Settlement ────────────────────────────────────────────────────────────────
  const handleSettlement = async (e) => {
    setLoading(true);
    e.preventDefault();
    if (isNaN(Number(settlementAmount)) || Number(settlementAmount) < 2 * Number(settlement?.loan_amt)) {
      setLoading(false);
      setErrorMsg(`Settlement amount must be at least double the loan amount (${2 * Number(settlement?.loan_amt)})`);
    } else {
      let settlements = await fetch(
        `${config.apiUrlBetaV2}get_waiver/${settlement?.loan_id}?setlement=${settlement?.setlement}&total_amt=${settlementAmount}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json', 'Authorization': localStorage.getItem('access_token') } }
      );
      settlements = await settlements.json();
      if (settlements.ok)              { setErrorMsg(false); setLoading(false); }
      if (settlements.status === false) { setErrorMsg(settlements.message); setLoading(false); setShowSuccessModal(false); }
      if (settlements.status === true)  { setShowSuccessModal(true); setLoading(false); }
      setLinkData(settlements);
      alert('Settlement successful');
    }
  };

  const openModal = async (settlementData) => {
    const token = localStorage.getItem('access_token');
    let loanData = await fetch(
      `${config.apiUrlBetaV2}get_waiver/${settlementData.loan_id}?percInterest=0&serperInterest=0`,
      { method: 'GET', headers: { 'Content-Type': 'application/json', 'Authorization': token } }
    );
    loanData = await loanData.json();
    setSettlementData(loanData);
    new window.bootstrap.Modal(modalRef.current).show();
  };

  // ── Remarks modal ─────────────────────────────────────────────────────────────
  const handleLoanClick = (loan_no, loanId) => {
    setSelectedLoanId(loanId);
    setLoan_no(loan_no);
    setShowModal(true);
  };
  const handleCloseModal2 = () => { setShowModal(false); setSelectedLoanId(null); };

  const handleSave = async (loanId, remarks, remarksStatus) => {
    const token = localStorage.getItem('access_token');
    await fetch(`${config.apiUrlBetaV2}update_remark`, {
      method: 'PUT',
      headers: { 'Authorization': token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ loanId, remarks, remarksStatus, agent_id: user_id._id }),
    });
  };

  // ── Loan detail modal ─────────────────────────────────────────────────────────
  const handleLoanClick1 = (loanId) => { setCog_id(loanId); setShowModal1(true); };
  const handleCloseModal1 = () => { setShowModal1(false); setSelectedLoanId(null); };

  // ── UI helpers ────────────────────────────────────────────────────────────────
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => alert('Copied to clipboard!')).catch(() => {});
  };

  const handleSelectChanges = (event) => {
    const value = event.target.value;
    setSelectedOptions(value);
    // reset dependent inputs
    setSearchText('');
    setAgencySelection('');
    setSelectionRange({ startDate: null, endDate: null, key: 'selection' });
    if (value === 'LoanDate' || value === 'DueDate') setOpenDate(true);
    else setOpenDate(false);
  };

  const toggleCalendar = () => setShowCalendar(!showCalendar);
  const handleSelects  = (ranges) => setSelectionRange(ranges.selection);
  const handleApply    = () => setShowCalendar(false);
  const handleHideMessage = () => setMessage('');

  // For the "Showing X to Y" line — use the actual page size
  const showingFrom = totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const showingTo   = Math.min(currentPage * PAGE_SIZE, totalCount);

  const pageNumbers = getPageNumbers();

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Toolbar ─────────────────────────────────────────────────────────── */}
      {loading ? (
        <span style={loadingTextStyle}><div style={spinnerStyle} /></span>
      ) : (
        <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">

          <div className="d-flex flex-wrap gap-4">

            {/* Select search type */}
            <div className="mb-2">
              <label className="form-label" htmlFor="grid-state">Select search</label>
              <select
                className="form-select w-auto"
                id="grid-state"
                value={selectedOptions}
                onChange={handleSelectChanges}
              >
                <option value="">---Select----</option>
                <option value="LoanDate">Loan Date</option>
                <option value="DueDate">Due Date</option>
                <option value="State">State</option>
                <option value="Mobile_Number">Mobile</option>
                <option value="Loan_Id">Loan Id</option>
              </select>
            </div>

            {/* Dynamic input */}
            <div className="mb-2">
              <label className="form-label">{selectedOptions || 'Enter here'}</label>

              {(selectedOptions === 'LoanDate' || selectedOptions === 'DueDate') ? (
                <div style={{ position: 'relative', width: '300px' }}>
                  <button
                    type="button"
                    onClick={toggleCalendar}
                    style={{
                      padding: '7px 6px',
                      border: '1px solid #ccc',
                      borderRadius: '5px',
                      width: '70%',
                      textAlign: 'left',
                      backgroundColor: '#fff',
                      cursor: 'pointer'
                    }}
                  >
                    {selectionRange.startDate && selectionRange.endDate
                      ? `${selectionRange.startDate.toLocaleDateString()} - ${selectionRange.endDate.toLocaleDateString()}`
                      : 'Select Date Range'}
                  </button>
                  {showCalendar && (
                    <div style={{
                      position: 'absolute',
                      top: '50px',
                      left: '0',
                      zIndex: 999,
                      backgroundColor: '#fff',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '5px'
                    }}>
                      <DateRangePicker
                        ranges={[{
                          startDate: selectionRange.startDate || new Date(),
                          endDate:   selectionRange.endDate   || new Date(),
                          key: 'selection'
                        }]}
                        onChange={handleSelects}
                        showDateDisplay={false}
                      />
                      <div style={{ marginTop: '10px', textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={handleApply}
                          style={{
                            padding: '5px 7px',
                            border: '1px solid #007BFF',
                            borderRadius: '5px',
                            backgroundColor: '#007BFF',
                            color: '#fff',
                            cursor: 'pointer'
                          }}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : selectedOptions === 'State' ? (
                <select
                  className="form-select w-auto"
                  id="AgencyId"
                  onChange={(e) => setAgencySelection(e.target.value)}
                  value={AgencySelection}
                >
                  <option value="" disabled>Select State</option>
                  <option value="Andhra Pradesh">Andhra Pradesh</option>
                  <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                  <option value="Assam">Assam</option>
                  <option value="Bihar">Bihar</option>
                  <option value="Chhattisgarh">Chhattisgarh</option>
                  <option value="Goa">Goa</option>
                  <option value="Gujarat">Gujarat</option>
                  <option value="Haryana">Haryana</option>
                  <option value="Himachal Pradesh">Himachal Pradesh</option>
                  <option value="Jharkhand">Jharkhand</option>
                  <option value="Karnataka">Karnataka</option>
                  <option value="Kerala">Kerala</option>
                  <option value="Maharashtra">Maharashtra</option>
                  <option value="Madhya Pradesh">Madhya Pradesh</option>
                  <option value="Manipur">Manipur</option>
                  <option value="Meghalaya">Meghalaya</option>
                  <option value="Mizoram">Mizoram</option>
                  <option value="Nagaland">Nagaland</option>
                  <option value="Odisha">Odisha</option>
                  <option value="Punjab">Punjab</option>
                  <option value="Rajasthan">Rajasthan</option>
                  <option value="Sikkim">Sikkim</option>
                  <option value="Tamil Nadu">Tamil Nadu</option>
                  <option value="Tripura">Tripura</option>
                  <option value="Telangana">Telangana</option>
                  <option value="Uttar Pradesh">Uttar Pradesh</option>
                  <option value="Uttarakhand">Uttarakhand</option>
                  <option value="West Bengal">West Bengal</option>
                  <option value="Agency3" disabled>-----UT-----</option>
                  <option value="Andaman & Nicobar">Andaman & Nicobar</option>
                  <option value="Dadra & Nagar Haveli">Dadra & Nagar Haveli</option>
                  <option value="Daman & Diu">Daman & Diu</option>
                  <option value="Jammu & Kashmir">Jammu & Kashmir</option>
                  <option value="Ladakh">Ladakh</option>
                  <option value="Lakshadweep">Lakshadweep</option>
                  <option value="Puducherry">Puducherry</option>
                </select>
              ) : (selectedOptions === 'Mobile_Number' || selectedOptions === 'Loan_Id') ? (
                <input
                  className="form-control w-auto"
                  id="LoanId"
                  type="text"
                  placeholder="Search here"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                />
              ) : (
                <input
                  className="form-control w-auto"
                  type="text"
                  placeholder="Select a search type first"
                  disabled
                />
              )}
            </div>

            {/* Days slab filter */}
            <div className="mb-2">
              <label className="form-label">Filter by Days Overdue</label>
              <select className="form-select w-auto" value={selectedSlab} onChange={handleSlabChange}>
                {SLAB_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Search + Clear buttons */}
            <div className="d-flex flex-wrap gap-2" style={{ marginTop: '30px' }}>
              <button
                type="button"
                onClick={handleSearch}
                className="btn btn-primary"
                style={{ height: '50px', width: '120px' }}
              >
                Search
              </button>
              <button
                type="button"
                onClick={handleClearSearch}
                className="btn btn-outline-secondary"
                style={{ height: '50px', width: '100px' }}
              >
                Clear
              </button>
            </div>
          </div>

          {/* Right: export */}
          <div className="d-flex flex-wrap align-items-center gap-3">
            {individual?.loan_details?.export_access === true && (
              <button className="btn btn-sm btn-primary-600" onClick={handleExport}>
                {loadingbutton ? (
                  <span style={loadingTextStyle}><div style={spinnerStyle} />downloading...</span>
                ) : (
                  <><i className="ri-add-line" /> Download Excel</>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Error banner ──────────────────────────────────────────────────────── */}
      {error && <div className="alert alert-danger mx-3 mt-2" role="alert">{error}</div>}

      {/* ── Table ─────────────────────────────────────────────────────────────── */}
      <div className="card-body">
        <div style={{ overflowX: 'auto', maxHeight: '750px', overflowY: 'auto' }}>
          <table className="table bordered-table mb-0">
            <thead style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#fff' }}>
              <tr>
                <th scope="col">LoanDate</th>
                <th scope="col">Last Login</th>
                <th scope="col">Loan Id</th>
                <th scope="col">Name</th>
                <th scope="col">Account Type</th>
                <th scope="col">Mobile Number</th>
                <th scope="col">Loan Amount</th>
                <th scope="col">College Name</th>
                <th scope="col">Close Count (Loan)</th>
                <th scope="col">Past Due Day</th>
                <th scope="col">Total Service Charge</th>
                <th scope="col">Total Daily Late Fee</th>
                <th scope="col">Total Penalty Charges</th>
                <th scope="col">Total Amount</th>
                <th scope="col">Total Payable Amount</th>
                <th scope="col">Partial Paid Amount</th>
                <th scope="col">Last Paid Date</th>
                <th scope="col">Amount Due</th>
                <th scope="col">Due Date</th>
                <th scope="col">State Code</th>
                <th scope="col">Pan Card</th>
                <th scope="col">Offer (20%)</th>
                <th scope="col">Offer (40%)</th>
                <th scope="col">Offer (50%)</th>
                <th scope="col">Offer (60%)</th>
                <th scope="col">Offer (70%)</th>
                <th scope="col">Offer (90%)</th>
                <th scope="col">Address</th>
                <th scope="col">Payment Link</th>
                <th scope="col" style={{ textAlign: 'center' }}>Offers</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 && !loading ? (
                <tr><td colSpan={31} className="text-center py-4 text-muted">No loans found.</td></tr>
              ) : (
                data.map((item, index) => {
                  const linkDate        = new Date(item.link_date);
                  const currentDate     = new Date();
                  const isLessThan24Hrs = (currentDate - linkDate) < (24 * 60 * 60 * 1000);
                  const rowClass        = isLessThan24Hrs ? 'bg-info' : 'bg-info';

                  return (
                    <tr key={item._id || index} className={rowClass}>
                      <td>{item.start ? new Date(item.start).toISOString().split('T')[0] : '—'}</td>
                      <td>{item.updatedOn ? new Date(item.updatedOn).toISOString().split('T')[0] : '—'}</td>
                      <td><Link to="#" className="text-primary-600">{item.loan_id}</Link></td>
                      <td>{item.Name ?? item?.student?.name ?? '—'}</td>
                      <td><Link to="#" className="text-primary-600">{item.user_acc_type ?? '—'}</Link></td>
                      <td>{item.Mobile ?? item?.student?.mobile ?? '—'}</td>
                      <td><h6 className="text-md mb-0 fw-medium flex-grow-1">{item.loan_amt}</h6></td>
                      <td>{item.college_name ?? '—'}</td>
                      <td><h6 className="text-md mb-0 fw-medium flex-grow-1">{item.loan_count ?? 0}</h6></td>
                      <td><h6 className="text-md mb-0 fw-medium flex-grow-1">{item.defaulted ?? '—'}</h6></td>
                      <td>{item.serviceChargeFee ?? 0}</td>
                      <td>{item.dailyLateFee ?? 0}</td>
                      <td>{item.totalPenalty ?? 0}</td>
                      <td>{item.totalAmountPayableOg || item.totalAmountPayable}</td>
                      <td>{item.totalAmountPayable}</td>
                      <td>{item.partial_total_paid}</td>
                      <td>{item?.last_partial_paid ? new Date(item.last_partial_paid).toISOString().split('T')[0] : 'NA'}</td>
                      <td>{item.totalAmountPayable}</td>
                      <td>{item.due ? new Date(item.due).toISOString().split('T')[0] : '—'}</td>
                      <td>{item.state_code ?? '—'}</td>
                      <td>{item.pan_number ?? '—'}</td>
                      <td>{item['20%']}</td>
                      <td>{item['40%']}</td>
                      <td>{item['50%']}</td>
                      <td>{item['60%']}</td>
                      <td>{item['70%']}</td>
                      <td>{item['90%']}</td>
                      <td>
                        <span className="bg-success-focus text-success-main px-24 py-4 rounded-pill fw-medium text-sm">
                          {item.Address || '—'}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <h6 className="text-md mb-0 fw-medium flex-grow-1">{item?.payment_link}</h6>
                          <button className="btn btn-sm btn-outline-primary" onClick={() => copyToClipboard(item?.payment_link)}>Copy</button>
                        </div>
                      </td>

                      {/* Offers column — Waiver + Settlement */}
                      <td>
                        <div className="d-flex align-items-center justify-content-start gap-3 flex-wrap">
                          {/* Waiver */}
                          <div
                            className="d-flex align-items-center gap-1 px-2 py-1 rounded bg-light border border-warning"
                            role="button"
                            onClick={() => handleWaiverClick(item.loan_id)}
                            title="Request Waiver"
                            style={{ cursor: 'pointer', transition: '0.2s ease' }}
                          >
                            <Icon icon="cil:paper-plane" style={{ fontSize: '20px', cursor: 'pointer' }} />
                            <i className="bi bi-percent text-warning fs-5" />
                            <span className="text-warning fw-medium small">Waiver</span>
                          </div>

                          {/* Settlement — only if defaulted >= 365 */}
                          {item.defaulted >= 365 && (
                            <div
                              className="d-flex align-items-center gap-1 px-2 py-1 rounded bg-light border border-success"
                              role="button"
                              onClick={() => {
                                const newSettlement = {
                                  loan_id: item.loan_id,
                                  loan_amt: item.loan_amt,
                                  student_cog_id: item.student_cog_id,
                                  setlement: true,
                                  total_amt: 2 * Number(item.loan_amt),
                                };
                                setSettlement(newSettlement);
                                openModal(newSettlement);
                              }}
                              title="Open Settlement Modal"
                              style={{ cursor: 'pointer', transition: '0.2s ease' }}
                            >
                              <i className="bi bi-cash-coin text-success fs-5" />
                              <span className="text-success fw-medium small">Settlement</span>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Action column */}
                      <td>
                        <Link to="#" className="w-32-px h-32-px me-8 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center">
                          <Icon icon="iconamoon:eye-light" onClick={() => handleLoanClick1(item.student_cog_id)} />
                        </Link>
                        <Link to="#" className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center">
                          <Icon icon="lucide:edit" onClick={() => handleLoanClick(item?.loan_id, item?._id)} />
                        </Link>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ──────────────────────────────────────────────────────── */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-24">
          <span>
            {totalCount > 0
              ? `Showing ${showingFrom} to ${showingTo} of ${totalCount} entries`
              : 'No entries'}
          </span>

          {totalPages > 1 && (
            <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center mb-0">
              {/* Prev */}
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  type="button"
                  className={`page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px me-8 w-32-px ${currentPage === 1 ? 'bg-base' : ''}`}
                  onClick={() => handlePageClick(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <Icon icon="ep:d-arrow-left" className="text-xl" />
                </button>
              </li>

              {/* Numeric / ellipsis */}
              {pageNumbers.map((page, idx) => (
                <li key={`${page}-${idx}`} className={`page-item ${page === currentPage ? 'active' : ''}`}>
                  {page === '...' ? (
                    <span className="page-link fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px me-8 w-32-px bg-primary-50 text-secondary-light"
                          style={{ cursor: 'default' }}>
                      …
                    </span>
                  ) : (
                    <button
                      type="button"
                      className={`page-link fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px me-8 w-32-px ${page === currentPage ? 'bg-primary-600 text-white' : 'bg-primary-50 text-secondary-light'}`}
                      onClick={() => handlePageClick(page)}
                      aria-current={page === currentPage ? 'page' : undefined}
                      aria-label={`Page ${page}`}
                    >
                      {page}
                    </button>
                  )}
                </li>
              ))}

              {/* Next */}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button
                  type="button"
                  className={`page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px me-8 w-32-px ${currentPage === totalPages ? 'bg-base' : ''}`}
                  onClick={() => handlePageClick(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  <Icon icon="ep:d-arrow-right" className="text-xl" />
                </button>
              </li>
            </ul>
          )}
        </div>
      </div>

      {/* ── Waiver Modal (Generate Link) ─────────────────────────────────────── */}
      <div className="modal fade" id="exampleModal" tabIndex={-1} aria-labelledby="exampleModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content radius-16 bg-base">
            <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0">
              <h1 className="modal-title fs-5" id="exampleModalLabel">Generate Link</h1>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>
            <div className="modal-body p-24">
              <form action="#">
                <div className="d-flex flex-wrap align-items-center gap-3 mb-5">
                  <p className="fw-semibold text-gray-500">Service %:</p>
                  <select id="service" className="form-select form-select-sm w-auto" defaultValue="service" ref={serviceSelect} onChange={handlecashfree}>
                    <option value="service" disabled>Select Service charge discount</option>
                    {percentage?.find(item => item?.type === 'service_fee')?.values.map((value, index) => (
                      <option key={index} value={value}>{value}%</option>
                    ))}
                  </select>
                  <p className="fw-semibold text-gray-500">Daily %:</p>
                  <select id="daily" className="form-select form-select-sm w-auto" defaultValue="daily" ref={dailySelect} onChange={handlecashfree}>
                    <option value="daily" disabled>Select Defaulted Loans</option>
                    {percentage?.find(item => item?.type === 'daily_late')?.values.map((value, index) => (
                      <option key={index} value={value}>{value}%</option>
                    ))}
                  </select>
                </div>

                {!loan_data ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                  </div>
                ) : (
                  <div className="row">
                    {[
                      ['Defaulting in days:', loan_data?.no_of_days_past_due],
                      ['Total Service Charge Fee:', loan_data?.serviceChargeFee],
                      ['Loan Amount:', loan_data?.loan_amt],
                      ['Total Daily Charge Fee:', loan_data?.dailyLateFee],
                      ['Total Penalty:', loan_data?.totalPenalty],
                      ['Total Amount Payable:', loan_data?.totalAmountPayable],
                      ['Service Charge Payable:', loan_data?.service_waived],
                      ['Daily Charge Payable:', loan_data?.waived],
                      ['Total Amount to be generated:', loan_data?.waived_total_amount],
                    ].map(([label, val], i) => (
                      <div key={i} className="d-flex align-items-center justify-content-between border border-gray-300 bg-gray-50 p-2 rounded shadow-sm">
                        <p className="fw-semibold text-gray-500">{label}</p>
                        <p className="text-right text-gray-600">{val}</p>
                      </div>
                    ))}
                    <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                      <button type="reset" className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-40 py-11 radius-8" data-bs-dismiss="modal" aria-label="Close">Cancel</button>
                      <button type="submit" className="btn btn-primary border border-primary-600 text-md px-48 py-12 radius-8"
                        onClick={() => handlecashfreelink(loan_data?.waived_total_amount, loan_data.loan_id, loan_data.waived, loan_data?.service_waived, Number(loan_data?.loan_amt) + Number(loan_data?.totalPenalty))}>
                        {loading ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> : 'Generate'}
                        {loading && ' Loading...'}
                      </button>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* ── Settlement Modal ─────────────────────────────────────────────────── */}
      <div className="modal fade" ref={modalRef} tabIndex="-1" aria-labelledby="settlementModalLabel" aria-hidden="true">
        <div className="modal-dialog">
          <div className="modal-content p-3">
            <div className="modal-header">
              <h5 className="modal-title" id="settlementModalLabel">Loan Summary & Settlement</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>
            <div className="modal-body">
              <div className="mb-3 border rounded p-3 bg-light shadow-sm">
                <h6 className="mb-3 text-primary">Loan Summary</h6>
                {[
                  ['Name:', settlementData?.data?.student?.name],
                  ['Mobile:', settlementData?.data?.student?.mobile],
                  ['Email:', settlementData?.data?.student?.email],
                  ['Loan Amount:', `₹${settlementData?.data?.loan_amt}`],
                  ['Loan ID:', settlementData?.data?.loan_id],
                  ['Service Charge:', `₹${settlementData?.data?.serviceChargeFee}`],
                  ['Daily Late Fee:', `₹${settlementData?.data?.dailyLateFee}`],
                  ['No of DPD:', settlementData?.data?.no_of_days_past_due],
                  ['DLF Slab:', settlementData?.data?.dailyDefaultFeeSlab],
                  ['SF Slab:', settlementData?.data?.service_charge],
                  ['Total Payable:', Number(settlementData?.data?.loan_amt) + Number(settlementData?.data?.totalPenalty)],
                ].map(([label, val], i) => (
                  <div key={i} className="row mb-2">
                    <div className="col-6"><strong>{label}</strong></div>
                    <div className="col-6 text-end text-muted">{val}</div>
                  </div>
                ))}
              </div>
              <SettlementInput student_id={settlementData?.data?.student_cog_id} />
              <input type="text" className="form-control mb-3" placeholder="Enter settlement amount" value={settlementAmount} onChange={(e) => setSettlementAmount(e.target.value)} />
              <div>
                <button className="btn btn-primary w-100" onClick={handleSettlement} disabled={loading}>
                  {loading ? <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" /> : null}
                  {loading ? 'Validating...' : 'Validate'}
                </button>
                {errorMsg && <div className="alert alert-danger mt-2" role="alert">{errorMsg}</div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Settlement Success Modal ──────────────────────────────────────────── */}
      {showSuccessModal && (
        <div className="modal fade show" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content p-3">
              <div className="modal-header">
                <h5 className="modal-title">Settlement Initiated</h5>
                <button type="button" className="btn-close" onClick={() => setShowSuccessModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3 border rounded p-3 bg-light shadow-sm">
                  <h6 className="mb-3 text-primary">Settlement Summary</h6>
                  {[
                    ['Loan Amount:', `₹${linkData.loan_amount}`],
                    ['Service Charge:', `₹${linkData.service_charge}`],
                    ['Daily Late Fee:', `₹${linkData.daily_fee}`],
                    ['Total Amount:', `₹${linkData.total_amount}`],
                    ['Adjust Amount:', `₹${linkData.monthlyAdjustment}`],
                  ].map(([label, val], i) => (
                    <div key={i} className="row mb-2">
                      <div className="col-6"><strong>{label}</strong></div>
                      <div className="col-6 text-end text-muted">{val}</div>
                    </div>
                  ))}
                </div>
                <div>
                  <button className="btn btn-primary w-100" disabled={loading}
                    onClick={async () => {
                      setLoading(true);
                      try {
                        await handlecashfreelink(linkData?.total_amount, settlementData.data.loan_id, linkData?.daily_fee, linkData?.service_charge, Number(settlementData?.data?.loan_amt) + Number(settlementData?.data?.totalPenalty));
                      } catch (err) { console.error(err); }
                      finally { setLoading(false); }
                    }}>
                    {loading ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Generating...</> : 'Generate Link'}
                  </button>
                  {message && <FailedMessage message={message} onHide={handleHideMessage} />}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Waiver Info Modal (already generated today) ───────────────────────── */}
      {showWaiverInfoModal && (
        <div className="modal fade show" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content p-3">
              <div className="modal-header">
                <h5 className="modal-title text-warning">⚠️ Waiver Link Already Generated Today</h5>
                <button type="button" className="btn-close" onClick={() => setShowWaiverInfoModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3 border rounded p-3 bg-light shadow-sm">
                  <h6 className="mb-3 text-primary">Existing Link Details</h6>
                  {[
                    ['Generated On:', waiverCheckData?.date ? new Date(waiverCheckData.date).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : 'NA'],
                    ['Total Payable:', `₹${waiverCheckData?.totalAmountPayable ?? 'NA'}`],
                    ['Waived Amount:', `₹${waiverCheckData?.waivedAmount?.toFixed(2) ?? 'NA'}`],
                    ['Service Charge Fee:', `₹${waiverCheckData?.service_charge_fee ?? 'NA'}`],
                    ['Daily Late Fee:', `₹${waiverCheckData?.daily_late_fee ?? 'NA'}`],
                  ].map(([label, val], i) => (
                    <div key={i} className="row mb-2">
                      <div className="col-6"><strong>{label}</strong></div>
                      <div className="col-6 text-end text-muted">{val}</div>
                    </div>
                  ))}
                  <div className="row mb-2">
                    <div className="col-6"><strong>Payment Link:</strong></div>
                    <div className="col-6 text-end">
                      <a href={waiverCheckData?.link_url} target="_blank" rel="noreferrer" className="text-primary text-break small">{waiverCheckData?.link_url ?? 'NA'}</a>
                    </div>
                  </div>
                  <div className="row mt-2">
                    <div className="col-12 text-center">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => copyToClipboard(waiverCheckData?.link_url)}>📋 Copy Link</button>
                    </div>
                  </div>
                </div>
                <button className="btn btn-warning w-100 mt-2"
                  onClick={() => {
                    setShowWaiverInfoModal(false);
                    setTimeout(() => {
                      const modalEl = document.getElementById('exampleModal');
                      new window.bootstrap.Modal(modalEl).show();
                    }, 300);
                  }}>
                  🔁 Generate Again with Different Offer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Remarks Modal ─────────────────────────────────────────────────────── */}
      {showModal && (
        <ModalEditRemarks show={showModal} handleClose={handleCloseModal2} handleSave={handleSave} loanId={loan_no} loan_no={selectedLoanId} />
      )}

      {/* ── Loan Detail Modal ─────────────────────────────────────────────────── */}
      {showModal1 && (
        <ModalLoan show={showModal1} handleClose={handleCloseModal1} CogID={cog_id} />
      )}
    </>
  );
};

export default ManagerLoanDetails;